import { useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { errorMessage, post } from "@/lib/api";
import type { CiteItem, CiteSource } from "@/features/notes/citeComplete";
import { formatAuthorsShort } from "./format";
import { libraryKey, useLibraryList } from "./useLibrary";
import type { LibraryRow } from "./types";

/** The key a citation uses: the paper's citation key, or the stable "bl-" key the exports use. */
export const citeKeyOf = (i: { id: string; citationKey: string | null }) => i.citationKey || `bl-${i.id.slice(0, 8)}`;

const toCite = (i: LibraryRow): CiteItem => ({ id: i.id, key: citeKeyOf(i), title: i.title || "Untitled source", authors: formatAuthorsShort(i.authors), year: i.year });

/** Papers of a notebook's or project's library, for \cite{ in its notes and manuscripts. */
export function useCiteSource(workspaceId: string | undefined): CiteSource {
  const qc = useQueryClient();
  const library = useLibraryList(workspaceId ?? "");
  const items = useRef<CiteItem[]>([]);
  items.current = (library.data?.items ?? []).map(toCite);
  const version = library.dataUpdatedAt;
  return useMemo<CiteSource>(
    () => ({
      version,
      items: () => items.current,
      addDoi: async (doi) => {
        if (!workspaceId) return null;
        try {
          const { results } = await post<{ results: { status: string; message: string; item?: { id: string } }[] }>(`/api/workspaces/${workspaceId}/library/from-doi`, { text: doi });
          const r = results[0];
          await qc.invalidateQueries({ queryKey: libraryKey(workspaceId) });
          const fresh = qc.getQueryData<{ items: LibraryRow[] }>(libraryKey(workspaceId))?.items.find((i) => i.id === r?.item?.id);
          if (!r?.item) toast.error(r?.message ?? "The paper could not be added.");
          else toast.success(r.message);
          return fresh ? toCite(fresh) : r?.item ? { id: r.item.id, key: `bl-${r.item.id.slice(0, 8)}`, title: doi, authors: "", year: null } : null;
        } catch (err) {
          toast.error(errorMessage(err));
          return null;
        }
      },
    }),
    [workspaceId, qc, version],
  );
}
